import { Button, FAIcon, ModuleWrapper } from '@/components'
import type {
  AllUserDocumentsListQueryVariables,
  AllUserDocumentsQuery,
} from '@/modules/users'
import { ALL_USER_DOCUMENTS_QUERY } from '@/modules/users/queries'
import { UserDocumentFilesModal } from '@/modules/users/UserDocuments/UserDocumentFilesModal'
import { PERMISSIONS } from '@/utils'
import {
  useDebounce,
  useNodes,
  useOnErrorAuto,
  usePermissions,
} from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { getUserDocumentFilterVariables } from './filter-variables'
import type { UserDocumentListFilterForm, UserDocumentRow } from './types'
import { emptyUserDocumentListFilter } from './types'
import { useUserDocumentHandlers } from './use-user-document-handlers'
import { UserDocumentFilters } from './user-document-filters'
import { UserDocumentsExportButton } from './user-documents-export-button'
import { UserDocumentsTable } from './user-documents-table'

export const UserDocumentsView: React.FC = () => {
  const translations = useTranslate({
    title: 'common.user-documents',
    searchPlaceholder: 'users.documents.search-placeholder',
    add: 'users.new-user-document',
  })

  const [filter, setFilter] = useState<UserDocumentListFilterForm>(
    emptyUserDocumentListFilter
  )

  // Only the free-text search is debounced; the other filters change one click
  // at a time and should apply immediately.
  const debouncedQ = useDebounce(filter.q)

  const filterVariables = useMemo(
    () => getUserDocumentFilterVariables({ ...filter, q: debouncedQ }),
    [filter, debouncedQ]
  )

  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery<
    AllUserDocumentsQuery,
    AllUserDocumentsListQueryVariables
  >(ALL_USER_DOCUMENTS_QUERY, {
    variables: { ...filterVariables, orderBy: 'expirationDate' },
    onError: onErrorAuto(),
  })

  const documents = useNodes(data?.allUserDocuments)

  const [filesDocument, setFilesDocument] = useState<Pick<
    UserDocumentRow,
    'id' | 'name'
  > | null>(null)
  const [filesModalOpen, setFilesModalOpen] = useState(false)

  function showFiles(document: Pick<UserDocumentRow, 'id' | 'name'>) {
    setFilesDocument(document)
    setFilesModalOpen(true)
  }

  const { addDocument, editDocument, removeDocument } = useUserDocumentHandlers(
    { onShowFiles: showFiles }
  )

  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(PERMISSIONS.users.add.userdocument, true)

  return (
    <ModuleWrapper
      title={translations.title}
      headerRight={
        <div className="flex items-center gap-2">
          <UserDocumentsExportButton filters={filterVariables} />
          {canAdd && (
            <Button onClick={() => void addDocument()}>
              <FAIcon icon="plus" />
              {translations.add}
            </Button>
          )}
        </div>
      }
    >
      <UserDocumentFilesModal
        open={filesModalOpen}
        document={filesDocument}
        onClose={() => setFilesModalOpen(false)}
      />

      <UserDocumentFilters
        filter={filter}
        searchPlaceholder={translations.searchPlaceholder}
        onChange={patch => setFilter(current => ({ ...current, ...patch }))}
        onClear={() => setFilter(emptyUserDocumentListFilter)}
      />

      <UserDocumentsTable
        documents={documents}
        loading={loading}
        onShowFiles={showFiles}
        onEdit={editDocument}
        onDelete={removeDocument}
      />
    </ModuleWrapper>
  )
}
